/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
} from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  of,
  startWith,
  Subject,
  switchMap,
  takeUntil,
} from "rxjs";

// PrimeNG imports
import { ConfirmationService, MessageService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { DialogService } from "primeng/dynamicdialog";
import { ToastModule } from "primeng/toast";
import { TooltipModule } from "primeng/tooltip";

// Our components and services
import { Site } from "../../../../core/models/site.interface";
import {
  QPilotAccessToken,
  QPilotService,
  QPilotUser,
} from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";
import { CustomerHistoryDialogComponent } from "../../components/customer-history-dialog/customer-history-dialog.component";
import { CustomersFiltersComponent } from "../../components/customers-filters/customers-filters.component";
import { ListPageSkeletonComponent } from "../../../../shared/components/table-skeleton";
import { CustomersTableComponent } from "../../components/customers-table/customers-table.component";
import {
  CustomerFilter,
  DEFAULT_CUSTOMER_FILTER_VALUES,
  ICustomerFilterControls,
} from "../../interfaces/customer-filter.interface";
import {
  CustomerListResponse,
  CustomerSummary,
} from "../../interfaces/customer.interface";
import { CustomersService } from "../../services/customers.service";
import {
  CustomerRowAction,
  PageEvent,
  SortEvent,
} from "../../components/customers-table/customers-table.component";

/**
 * Customers List Page Component
 * Main orchestrating component that manages filters, table, and navigation
 * Following scheduled-orders-list pattern
 */
@Component({
  selector: "app-customers-list",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ToastModule,
    ConfirmDialogModule,
    ButtonModule,
    TooltipModule,
    CustomersFiltersComponent,
    ListPageSkeletonComponent,
    CustomersTableComponent,
  ],
  templateUrl: "./customers-list.component.html",
  styleUrl: "./customers-list.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService, DialogService],
  // Note: MessageService is NOT provided locally - we use the root instance
  // This ensures both component toast calls AND API interceptor error toasts
  // go to the same MessageService that the <p-toast> listens to
})
export class CustomersListComponent implements OnInit, OnDestroy {
  // Dual mode inputs for web component/iframe support
  @Input() inputSiteId?: number;
  @Input() inputToken?: QPilotAccessToken;
  @Input() embeddedMode: boolean = false;

  private readonly elementRef = inject(ElementRef);
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly customersService = inject(CustomersService);
  private readonly sessionService = inject(SessionService);
  private readonly qpilotService = inject(QPilotService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly dialogService = inject(DialogService);

  // Reactive state using signals
  readonly currentSiteId = signal<number | null>(null);
  readonly currentSite = signal<Site | null>(null);

  // Data signals
  private readonly customersData = signal<CustomerListResponse | null>(null);

  // UI state
  readonly sidebarCollapsed = signal(false);
  private userManuallyCollapsed = false;

  // Signal to skip localStorage load when navigating with URL search params
  readonly skipLocalStorageLoad = signal(false);

  // Loading states
  private readonly isLoading = signal(false);
  private readonly isInitialLoading = signal(true);

  // Form management
  filterForm: FormGroup<ICustomerFilterControls>;

  // Signal to track form value changes (for computed signals to react to)
  private readonly formValues = signal<
    Partial<{
      search: string | null;
      includeDeleted: boolean;
      customerCreatedRange: Date[] | null;
    }>
  >({});

  // Computed values
  readonly data = computed(() => this.customersData());
  readonly loading = computed(() => this.isLoading());
  readonly initialLoading = computed(() => this.isInitialLoading());
  /**
   * Check if any filters are active
   * Matches old QMC filters: search, date range, isDeleted
   * Uses formValues signal to ensure reactivity with OnPush change detection
   */
  readonly hasActiveFilters = computed(() => {
    // Access the signal to create a reactive dependency
    const values = this.formValues();

    return !!(
      values.search?.trim() ||
      values.includeDeleted ||
      (Array.isArray(values.customerCreatedRange) &&
        values.customerCreatedRange.length > 0)
    );
  });

  // Component lifecycle
  private readonly destroy$ = new Subject<void>();

  constructor() {
    // Initialize form in constructor
    // Matches old QMC filters: search, date range (Start Date/End Date), isDeleted
    this.filterForm = this.fb.group<ICustomerFilterControls>({
      siteId: this.fb.nonNullable.control(0),
      search: this.fb.control(null),
      customerCreatedRange: this.fb.control(null),
      includeDeleted: this.fb.nonNullable.control(false),
      page: this.fb.nonNullable.control(DEFAULT_CUSTOMER_FILTER_VALUES.page),
      pageSize: this.fb.nonNullable.control(
        DEFAULT_CUSTOMER_FILTER_VALUES.pageSize,
      ),
      sortBy: this.fb.control(DEFAULT_CUSTOMER_FILTER_VALUES.sortBy),
      sortDirection: this.fb.nonNullable.control(
        DEFAULT_CUSTOMER_FILTER_VALUES.sortDirection,
      ),
    });
  }

  ngOnInit(): void {
    // Detect embedded mode from attribute (for web components)
    const element = this.elementRef.nativeElement as HTMLElement;
    const embeddedModeAttr =
      element.getAttribute("embedded-mode") ??
      element.getAttribute("embeddedMode");
    if (embeddedModeAttr === "true" || embeddedModeAttr === "") {
      this.embeddedMode = true;
    }

    // Get token from attribute in embedded mode
    if (this.embeddedMode) {
      const tokenString =
        element.getAttribute("input-token") ??
        element.getAttribute("inputToken");

      // Create inputToken from attribute if:
      // 1. We have a token string from attribute
      // 2. AND inputToken is either missing, not an object, or doesn't have valid TokenBearerAuth
      // Note: Angular Elements may bind the attribute as a raw string, so we check for proper structure
      if (
        tokenString &&
        (!this.inputToken ||
          typeof this.inputToken !== "object" ||
          !this.inputToken.TokenBearerAuth)
      ) {
        this.inputToken = {
          TokenBearerAuth: tokenString,
          TokenAuth: "",
          Id: 0,
          UserId: 0,
          CreatedUtc: new Date(),
          UpdatedUtc: new Date(),
          ExpiresUtc: new Date(),
          User: {} as QPilotUser,
          FirstLogin: 0,
        };
      }
    }

    this.initializeSiteId();
    this.initializeResponsiveSidebar();
    this.setupWindowResizeListener();
    this.setupQueryParamsSubscription();
    this.loadPersistedFiltersToForm();
    this.setupDataSubscriptions();
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Subscribe to queryParams changes to handle command palette navigation
   * This allows the search filter to be updated when navigating with ?search= params
   */
  private setupQueryParamsSubscription(): void {
    // Skip for embedded mode - it uses inputSearch instead
    if (this.embeddedMode) return;

    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const searchParam = params["search"];
        const timestampParam = params["_t"]; // Used by command palette to force refresh

        // Only react if there's a search param AND it's different from current value
        // OR if there's a timestamp param (force refresh from command palette)
        if (searchParam !== undefined || timestampParam) {
          const currentSearch = this.filterForm.get("search")?.value;

          // If search param changed or there's a timestamp (force refresh)
          if (searchParam !== currentSearch || timestampParam) {
            this.skipLocalStorageLoad.set(true);
            this.filterForm.patchValue(
              {
                search: searchParam || "",
                customerCreatedRange: null,
                includeDeleted: false,
                page: 1,
              },
              { emitEvent: true }, // Emit event to trigger data reload
            );
          }
        }
      });
  }

  /**
   * Get access token based on mode (embedded vs standard)
   */
  private getAccessToken(): QPilotAccessToken | undefined {
    if (this.embeddedMode && this.inputToken) {
      return this.inputToken;
    }
    return this.sessionService.accessToken ?? undefined;
  }

  /**
   * Initialize site ID from route parameters or input (embedded mode)
   * Old QMC pattern: Load site to get UtcOffset for timezone-aware queries
   */
  private initializeSiteId(): void {
    let siteId: number | null = null;

    if (this.embeddedMode) {
      // Embedded mode: Get siteId from input or attribute
      const element = this.elementRef.nativeElement as HTMLElement;
      const siteIdString =
        this.inputSiteId?.toString() ??
        element.getAttribute("input-site-id") ??
        element.getAttribute("inputSiteId");

      if (siteIdString) {
        siteId = parseInt(siteIdString, 10);
      }
    } else {
      // Standard mode: Get site ID from parent route parameter
      siteId = Number(this.route.parent?.snapshot.paramMap.get("siteId"));
    }

    if (siteId && siteId > 0) {
      this.currentSiteId.set(siteId);
      this.filterForm.patchValue({ siteId }, { emitEvent: false });

      // Load site data to get UtcOffset (matching old QMC pattern)
      const accessToken = this.getAccessToken();
      if (accessToken) {
        this.qpilotService
          .getSite(siteId, accessToken)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (site) => {
              this.currentSite.set(site as Site);
              this.cdr.markForCheck();
            },
            error: (err) => {
              console.error("Failed to load site:", err);
            },
          });
      }
    } else {
      console.warn("No siteId found in route parameters or input");
    }
  }

  /**
   * Setup reactive data subscriptions
   */
  private setupDataSubscriptions(): void {
    let isFirstLoad = true;

    // Update formValues signal on every form change (for hasActiveFilters computed)
    // This subscription runs immediately without debounce for responsive UI
    this.filterForm.valueChanges
      .pipe(startWith(this.filterForm.value), takeUntil(this.destroy$))
      .subscribe((formValue) => {
        this.formValues.set({
          search: formValue.search,
          includeDeleted: formValue.includeDeleted,
          customerCreatedRange: formValue.customerCreatedRange,
        });
      });

    // Debounced subscription for API calls
    this.filterForm.valueChanges
      .pipe(
        startWith(this.filterForm.value),
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((formValue) => {
          if (!formValue.siteId) {
            return of(null);
          }

          // Check if date range is incomplete
          const hasIncompleteDateRange =
            Array.isArray(formValue.customerCreatedRange) &&
            formValue.customerCreatedRange.length === 2 &&
            formValue.customerCreatedRange[0] &&
            !formValue.customerCreatedRange[1];

          if (hasIncompleteDateRange) {
            return of({ skipUpdate: true });
          }

          // Set loading states
          if (isFirstLoad) {
            this.isInitialLoading.set(true);
          }
          this.isLoading.set(true);

          const filter = this.buildFilterFromForm(formValue);
          const accessToken = this.getAccessToken();

          return this.customersService.getCustomers(filter, accessToken).pipe(
            catchError((error) => {
              console.error("Error loading customers:", error);
              this.messageService.add({
                severity: "error",
                summary: "Error",
                detail: "Failed to load customers. Please try again.",
                life: 5000,
              });
              return of(null);
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (result) => {
          // Skip if incomplete date range
          if (result && "skipUpdate" in result) {
            return;
          }

          this.customersData.set(result);
          this.isLoading.set(false);

          if (isFirstLoad) {
            this.isInitialLoading.set(false);
            isFirstLoad = false;
          }

          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error("Subscription error:", error);
          this.isLoading.set(false);

          if (isFirstLoad) {
            this.isInitialLoading.set(false);
            isFirstLoad = false;
          }

          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Load initial data
   */
  private loadInitialData(): void {
    this.filterForm.markAsDirty();
    this.filterForm.updateValueAndValidity();
  }

  /**
   * Load persisted filters from localStorage
   * Matches old QMC filters: search, date range, isDeleted
   * Checks for URL search param first (used by command palette navigation)
   */
  private loadPersistedFiltersToForm(): void {
    // Check for search query param (used when navigating from command palette)
    const searchParam = this.embeddedMode
      ? undefined
      : this.route.snapshot.queryParams["search"];

    if (searchParam) {
      // Search param exists - use only the search value, reset everything else
      // Also signal to skip loading from localStorage
      this.skipLocalStorageLoad.set(true);
      this.filterForm.patchValue(
        {
          search: searchParam,
          customerCreatedRange: null,
          includeDeleted: false,
          page: 1,
        },
        { emitEvent: false },
      );
      return;
    }

    // Skip localStorage if already loaded from URL params
    if (this.skipLocalStorageLoad()) {
      return;
    }

    try {
      const saved = localStorage.getItem("customers.filters");
      if (saved) {
        const filters = JSON.parse(saved);

        this.filterForm.patchValue(
          {
            search: filters.search,
            includeDeleted: filters.includeDeleted || false,
            customerCreatedRange: this.deserializeDateRangeFromStorage(
              filters.customerCreatedRange,
            ),
          },
          { emitEvent: false },
        );
      }
    } catch (error) {
      console.warn("Failed to load persisted filters:", error);
    }
  }

  /**
   * Deserialize date range from localStorage
   */
  private deserializeDateRangeFromStorage(
    range: {
      startDate: string | null;
      endDate: string | null;
    } | null,
  ): Date[] | null {
    if (!range || !range.startDate) return null;

    const dates: Date[] = [new Date(range.startDate)];
    if (range.endDate) {
      dates.push(new Date(range.endDate));
    }
    return dates;
  }

  /**
   * Build API filter from form values
   * Matches old QMC pattern: page=1&pageSize=20&orderBy=Id&order=asc&status=Active&offset=-5&active=true&valid=true
   * Old QMC filters: search, Start Date/End Date, Filter by Deleted
   */
  private buildFilterFromForm(
    formValue: Partial<{
      siteId: number;
      search: string | null;
      customerCreatedRange: Date[] | null;
      includeDeleted: boolean;
      page: number;
      pageSize: number;
      sortBy: string | null;
      sortDirection: "asc" | "desc";
    }>,
  ): CustomerFilter {
    // Get site UtcOffset (defaults to 0 if not loaded yet)
    const siteUtcOffset = this.currentSite()?.UtcOffset ?? 0;

    const filter: CustomerFilter = {
      siteId: formValue.siteId || 0,
      pageSize: formValue.pageSize || DEFAULT_CUSTOMER_FILTER_VALUES.pageSize,
      page: formValue.page || DEFAULT_CUSTOMER_FILTER_VALUES.page,

      // Sorting - Old QMC defaults: orderBy=Id&order=asc
      orderBy: formValue.sortBy || DEFAULT_CUSTOMER_FILTER_VALUES.sortBy,
      order:
        formValue.sortDirection || DEFAULT_CUSTOMER_FILTER_VALUES.sortDirection,

      // Status filter - Old QMC defaults to "Active"
      status: DEFAULT_CUSTOMER_FILTER_VALUES.status,

      // Timezone offset from site - Old QMC: offset=site.UtcOffset
      offset: siteUtcOffset,

      // Active/Valid filters - Old QMC defaults: active=true, valid=true
      // (includeInactive=false → active=true, includeInvalid=false → valid=true)
      includeInactive: DEFAULT_CUSTOMER_FILTER_VALUES.includeInactive,
      includeInvalid: DEFAULT_CUSTOMER_FILTER_VALUES.includeInvalid,

      // Search - Old QMC: search input
      search: formValue.search?.trim() || undefined,

      // Deleted filter - Old QMC: isDeleted checkbox
      isDeleted: formValue.includeDeleted || undefined,
    };

    // Date range - Old QMC: Start Date / End Date
    const range = formValue.customerCreatedRange;
    if (Array.isArray(range) && range.length === 2 && range[0] && range[1]) {
      const normalizedStart = new Date(range[0]);
      normalizedStart.setHours(0, 0, 0, 0);

      const normalizedEnd = new Date(range[1]);
      normalizedEnd.setHours(23, 59, 59, 999);

      filter.customerCreatedFromDate = normalizedStart;
      filter.customerCreatedToDate = normalizedEnd;
    }

    return filter;
  }

  /**
   * Handle filter changes from child component
   */
  onFiltersChanged(filter: CustomerFilter): void {
    // Reset to first page when filters change
    this.filterForm.patchValue({ page: 1 }, { emitEvent: false });
  }

  /**
   * Handle clear filters from child component
   * Matches old QMC filters: search, date range, isDeleted
   */
  onClearFilters(): void {
    this.filterForm.patchValue({
      search: null,
      customerCreatedRange: null,
      includeDeleted: false,
      page: DEFAULT_CUSTOMER_FILTER_VALUES.page,
      pageSize: DEFAULT_CUSTOMER_FILTER_VALUES.pageSize,
      sortBy: DEFAULT_CUSTOMER_FILTER_VALUES.sortBy,
      sortDirection: DEFAULT_CUSTOMER_FILTER_VALUES.sortDirection,
    });
  }

  /**
   * Handle table sorting
   */
  onSortChanged(event: SortEvent): void {
    this.filterForm.patchValue({
      sortBy: event.field,
      sortDirection: event.order === 1 ? "asc" : "desc",
      page: 1, // Reset to first page
    });
  }

  /**
   * Handle table pagination
   */
  onPageChanged(event: PageEvent): void {
    const currentPage =
      event.page !== undefined
        ? event.page
        : Math.floor(event.first / event.rows);

    this.filterForm.patchValue({
      page: currentPage + 1, // PrimeNG uses 0-based pages, API uses 1-based
      pageSize: event.rows,
    });
  }

  /**
   * Handle row actions from table
   */
  onRowAction(event: {
    action: CustomerRowAction;
    customer: CustomerSummary;
    searchTerm?: string;
  }): void {
    const { action, customer, searchTerm } = event;

    switch (action) {
      case "edit":
        this.editCustomer(customer);
        break;
      case "viewScheduledOrders":
        this.viewScheduledOrders(customer);
        break;
      case "searchScheduledOrders":
        this.searchScheduledOrders(searchTerm || "");
        break;
      case "viewHistory":
        this.viewCustomerHistory(customer);
        break;
      case "delete":
        this.deleteCustomer(customer);
        break;
      case "restore":
        this.restoreCustomer(customer);
        break;
      default:
        console.warn("Unknown row action:", action);
    }
  }

  /**
   * Edit customer
   * Uses PascalCase Id matching API response
   * Emits event in embedded mode for parent to handle navigation
   */
  private editCustomer(customer: CustomerSummary): void {
    const siteId = this.currentSiteId();
    if (!siteId) return;

    if (this.embeddedMode) {
      // Emit event for parent to handle navigation
      const event = new CustomEvent("qmc-navigate", {
        detail: {
          component: "customers-list",
          action: "edit",
          customerId: customer.Id,
          siteId: siteId,
        },
        bubbles: true,
        composed: true, // Crosses shadow DOM boundary
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else {
      // Use standard routing
      void this.router.navigate([
        "/widgets",
        "dashboard",
        siteId,
        "customers",
        customer.Id,
        "edit",
      ]);
    }
  }

  /**
   * View customer's scheduled orders
   * Navigates to scheduled orders page with customer email in URL query param
   * The scheduled orders page will detect the query param and reset filters
   * Emits event in embedded mode for parent to handle navigation
   */
  private viewScheduledOrders(customer: CustomerSummary): void {
    const siteId = this.currentSiteId();
    if (!siteId) return;

    if (this.embeddedMode) {
      // Emit event for parent to handle navigation
      const event = new CustomEvent("qmc-navigate", {
        detail: {
          component: "customers-list",
          action: "viewScheduledOrders",
          customerId: customer.Id,
          customerEmail: customer.Email,
          siteId: siteId,
        },
        bubbles: true,
        composed: true,
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else {
      // Navigate with search query param - scheduled orders page will handle filter reset
      void this.router.navigate(
        ["/widgets", "dashboard", siteId, "scheduled-orders"],
        { queryParams: { search: customer.Email } },
      );
    }
  }

  /**
   * Search scheduled orders by a specific term (name or email)
   * Navigates to scheduled orders page with custom search term in URL query param
   * Emits event in embedded mode for parent to handle navigation
   */
  private searchScheduledOrders(searchTerm: string): void {
    const siteId = this.currentSiteId();
    if (!siteId || !searchTerm) return;

    if (this.embeddedMode) {
      // Emit event for parent to handle navigation
      const event = new CustomEvent("qmc-navigate", {
        detail: {
          component: "customers-list",
          action: "searchScheduledOrders",
          searchTerm: searchTerm,
          siteId: siteId,
        },
        bubbles: true,
        composed: true,
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else {
      void this.router.navigate(
        ["/widgets", "dashboard", siteId, "scheduled-orders"],
        { queryParams: { search: searchTerm } },
      );
    }
  }

  /**
   * View customer history dialog
   * Passes embedded mode context if available
   */
  private viewCustomerHistory(customer: CustomerSummary): void {
    const displayName = this.getCustomerDisplayName(customer);
    const siteId = this.currentSiteId();

    this.dialogService.open(CustomerHistoryDialogComponent, {
      header: `History: ${displayName}`,
      width: "min(700px, 95vw)",
      modal: true,
      closable: true,
      contentStyle: { overflow: "visible", padding: "1rem" },
      data: {
        customerId: customer.Id,
        siteId: siteId,
        embeddedMode: this.embeddedMode,
        inputToken: this.inputToken,
      },
    });
  }

  /**
   * Get customer display name for dialogs
   */
  private getCustomerDisplayName(customer: CustomerSummary): string {
    const firstName = customer.FirstName?.trim() || "";
    const lastName = customer.LastName?.trim() || "";
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    }
    return customer.Email || `Customer #${customer.Id}`;
  }

  /**
   * Delete customer (soft delete)
   * Uses PascalCase properties matching API response
   */
  private deleteCustomer(customer: CustomerSummary): void {
    const displayName = this.getCustomerDisplayName(customer);
    this.confirmationService.confirm({
      message: `Are you sure you want to delete customer "${displayName}"? This action can be undone by restoring the customer.`,
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      acceptIcon: "pi pi-trash",
      acceptButtonStyleClass: "p-button-danger",
      acceptLabel: "Yes, delete",
      rejectLabel: "Cancel",
      accept: () => {
        this.customersService
          .softDeleteCustomer(customer.Id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: "success",
                summary: "Success",
                detail: "Customer deleted successfully",
              });
              this.refreshData();
            },
            error: (error) => {
              console.error("Delete failed:", error);
              this.messageService.add({
                severity: "error",
                summary: "Error",
                detail: "Failed to delete customer. Please try again.",
                life: 5000,
              });
            },
          });
      },
    });
  }

  /**
   * Restore soft-deleted customer
   * Uses PascalCase properties matching API response
   */
  private restoreCustomer(customer: CustomerSummary): void {
    const displayName = this.getCustomerDisplayName(customer);
    this.confirmationService.confirm({
      message: `Are you sure you want to restore customer "${displayName}"?`,
      header: "Confirm Restore",
      icon: "pi pi-refresh",
      acceptIcon: "pi pi-check",
      acceptButtonStyleClass: "p-button-success",
      acceptLabel: "Yes, restore",
      rejectLabel: "Cancel",
      accept: () => {
        this.customersService
          .restoreCustomer(customer.Id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: "success",
                summary: "Success",
                detail: "Customer restored successfully",
              });
              this.refreshData();
            },
            error: (error) => {
              console.error("Restore failed:", error);
              this.messageService.add({
                severity: "error",
                summary: "Error",
                detail: "Failed to restore customer. Please try again.",
                life: 5000,
              });
            },
          });
      },
    });
  }

  /**
   * Refresh data by re-triggering form changes
   */
  private refreshData(): void {
    this.filterForm.markAsDirty();
    this.filterForm.updateValueAndValidity();
  }

  /**
   * Toggle sidebar visibility
   */
  toggleSidebar(): void {
    const newState = !this.sidebarCollapsed();
    this.sidebarCollapsed.set(newState);
    this.userManuallyCollapsed = newState;
  }

  /**
   * Initialize sidebar state based on screen size
   */
  private initializeResponsiveSidebar(): void {
    const isMobile = window.innerWidth <= 768;
    this.sidebarCollapsed.set(isMobile);
  }

  /**
   * Setup window resize listener for responsive behavior
   */
  private setupWindowResizeListener(): void {
    const resizeObserver = new ResizeObserver(() => {
      const isMobile = window.innerWidth <= 768;

      if (isMobile && !this.userManuallyCollapsed) {
        this.sidebarCollapsed.set(true);
      } else if (!isMobile && !this.userManuallyCollapsed) {
        this.sidebarCollapsed.set(false);
      }

      this.cdr.markForCheck();
    });

    resizeObserver.observe(document.body);

    this.destroy$.subscribe(() => {
      resizeObserver.disconnect();
    });
  }
}
